import { useEffect, useRef, useState } from 'react';

import './laundry-game.css';
import { SockColor, missingColors } from './laundry_game_defs';
import { SOCK_IMAGES } from './laundry_game_defs';
import LaundryBasket from '../../assets/images/laundry_game/laundry_basket.png';

const EDGE_PADDING = 30;
const MEAN_DROP_INTERVAL = 1000;
const DROP_VARIANCE = MEAN_DROP_INTERVAL * 0.5;
const GRAVITY = 0.0005;

interface Sock {
    color: SockColor;
    startedFalling: number;
    x: number;
    y: number;
}

interface PlayingGameProps {
    gameBounds: DOMRect;
    caughtSocks: SockColor[];
    caughtSock: (color: SockColor) => void;
}

function getNewObject(caughtColors: SockColor[], gameWidth: number): Sock {
    const singleSock =
        caughtColors.length % 2 === 1
            ? caughtColors[caughtColors.length - 1]
            : undefined;
    const missing = missingColors(caughtColors).filter(
        (color) => color !== singleSock
    );
    const rand = Math.random();
    let color: SockColor;
    // Purely random colors make the game kinda frustrating at the beginning, because
    // you might have to wait a while for the color you need to show up.
    // To address this, we weight the sock you're looking for a little more heavily.
    if (singleSock !== undefined && rand < (1 / missing.length) * 1.5) {
        color = singleSock;
    } else {
        // Otherwise, uniformly sample among the remaining colors.
        color = missing[Math.floor(Math.random() * missing.length)];
    }

    return {
        color,
        startedFalling: new Date().getTime(),
        x: Math.random() * (gameWidth - 2 * EDGE_PADDING) + EDGE_PADDING,
        y: 0,
    };
}

function caught(
    sockPos: { x: number; y: number },
    basket: HTMLDivElement | null,
    gameBounds: DOMRect
): boolean {
    const basketBounds = basket?.getBoundingClientRect();
    if (!basketBounds) {
        return false;
    }

    if (
        sockPos.x < basketBounds.right - gameBounds.left &&
        sockPos.x > basketBounds.left - gameBounds.left &&
        sockPos.y < basketBounds.bottom - gameBounds.top &&
        sockPos.y > basketBounds.top - gameBounds.top
    ) {
        return true;
    }

    return false;
}

function PlayingGame(props: PlayingGameProps) {
    const { gameBounds, caughtSocks, caughtSock } = props;
    const [fallingObjects, setFallingObjects] = useState<Sock[]>([]);
    const [mouseX, setMouseX] = useState(0);
    const basketRef = useRef<HTMLImageElement | null>(null);
    const lastDrop = useRef<number>(0);

    useEffect(() => {
        let currentTimeout: ReturnType<typeof setTimeout>;
        function dropObject() {
            const now = new Date().getTime();
            if (now - lastDrop.current >= MEAN_DROP_INTERVAL - DROP_VARIANCE) {
                setFallingObjects((objects) => {
                    // Don't drop socks in any color where we already have a pair.
                    const newObject = getNewObject(
                        caughtSocks,
                        gameBounds.width
                    );
                    return [...objects, newObject];
                });
                lastDrop.current = now;
            }
            currentTimeout = setTimeout(
                dropObject,
                MEAN_DROP_INTERVAL + DROP_VARIANCE * (Math.random() - 0.5) * 2
            );
        }

        dropObject();
        return () => {
            clearTimeout(currentTimeout);
        };
    }, [caughtSocks, gameBounds]);

    useEffect(() => {
        let frameRequest: number;

        function tick() {
            setFallingObjects((objects) => {
                return objects.map((object) => ({
                    ...object,
                    y:
                        0.5 *
                        GRAVITY *
                        Math.pow(
                            new Date().getTime() - object.startedFalling,
                            2
                        ),
                }));
            });
            frameRequest = window.requestAnimationFrame(tick);
        }

        frameRequest = window.requestAnimationFrame(tick);

        return () => {
            window.cancelAnimationFrame(frameRequest);
        };
    }, []);

    useEffect(() => {
        function listener(event: MouseEvent) {
            setMouseX(event.x);
        }

        window.addEventListener('mousemove', listener);

        return () => window.removeEventListener('mousemove', listener);
    }, []);

    useEffect(() => {
        const newFallingObjects = fallingObjects.filter(
            (sock) =>
                !caught(sock, basketRef.current, gameBounds) &&
                sock.y < gameBounds.height
        );
        if (newFallingObjects.length !== fallingObjects.length) {
            setFallingObjects(newFallingObjects);
        }
        fallingObjects.forEach((object) => {
            if (caught(object, basketRef.current, gameBounds)) {
                caughtSock(object.color);
            }
        });
    }, [fallingObjects, gameBounds.height, caughtSock, gameBounds]);

    return (
        <>
            {fallingObjects.map((object, i) => (
                <div
                    key={i}
                    className="falling-object"
                    style={{ top: object.y, left: object.x }}
                >
                    <img
                        className="sock-image"
                        src={SOCK_IMAGES[object.color]}
                        alt={`${SockColor[object.color]} sock`}
                    />
                </div>
            ))}
            <img
                className="laundry-basket"
                style={{
                    top: '70%',
                    left: `calc(${mouseX}px - 14%)`,
                    width: '18%',
                }}
                ref={basketRef}
                alt="laundry basket"
                src={LaundryBasket}
            />
        </>
    );
}

export default PlayingGame;
